import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { adminApi } from "@profitlens/api-client";
import type { AlertOut } from "@profitlens/api-client";
import { Badge } from "@profitlens/ui/badge";
import { Button } from "@profitlens/ui/button";
import { Card } from "@profitlens/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@profitlens/ui/dialog";
import { ErrorState } from "@profitlens/ui/error-state";
import { Input } from "@profitlens/ui/input";
import { Label } from "@profitlens/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@profitlens/ui/select";
import { Spinner } from "@profitlens/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@profitlens/ui/table";

import { PageHeader } from "@/components/page-header";
import { Pagination } from "@/components/pagination";
import { translateError } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";

const PAGE_SIZE = 20;
const ALL = "__all__";

const TYPE_KEYS: Record<string, string> = {
  ai_cost_exceeded: "ops.typeAiCostExceeded",
  sync_failed: "ops.typeSyncFailed",
  plan_expired: "ops.typePlanExpired",
  payment_failed: "ops.typePaymentFailed",
  payment_mismatch: "ops.typePaymentMismatch",
  system: "ops.typeSystem",
};

function SeverityBadge({ severity }: { severity: string }) {
  const { t } = useTranslation();
  if (severity === "critical") return <Badge variant="danger">{t("ops.severityCritical")}</Badge>;
  if (severity === "warning") return <Badge variant="warning">{t("ops.severityWarning")}</Badge>;
  return <Badge variant="secondary">{t("ops.severityInfo")}</Badge>;
}

export function AlertsPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"open" | "resolved" | typeof ALL>("open");
  const [page, setPage] = useState(1);
  const [resolving, setResolving] = useState<AlertOut | null>(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: ["admin", "alerts", { status, page }],
    queryFn: () =>
      adminApi.alerts({
        status: status === ALL ? undefined : status,
        page,
        page_size: PAGE_SIZE,
      }),
  });

  const resolve = useMutation({
    mutationFn: () => {
      if (!resolving) throw new Error("no alert");
      return adminApi.resolveAlert(resolving.id, { note: note || null });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin", "alerts"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "stats"] }),
      ]);
      setResolving(null);
      setNote("");
      setError(null);
    },
    onError: (err) => setError(translateError(err, t)),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("ops.alertsTitle")}
        subtitle={t("ops.alertsSubtitle")}
        actions={
          <Select
            value={status}
            onValueChange={(v) => {
              setStatus(v as typeof status);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">{t("ops.alertOpen")}</SelectItem>
              <SelectItem value="resolved">{t("ops.alertResolved")}</SelectItem>
              <SelectItem value={ALL}>{t("common.all")}</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      {list.isLoading ? (
        <div className="grid place-items-center py-12">
          <Spinner className="h-5 w-5" />
        </div>
      ) : list.error ? (
        <ErrorState error={list.error} onRetry={() => list.refetch()} />
      ) : list.data && list.data.items.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted">{t("ops.alertsEmpty")}</Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("ops.announcementLevel")}</TableHead>
                <TableHead>{t("ops.alertType")}</TableHead>
                <TableHead>{t("common.tenant")}</TableHead>
                <TableHead>{t("ops.alertMessage")}</TableHead>
                <TableHead>{t("ops.alertCount")}</TableHead>
                <TableHead>{t("common.time")}</TableHead>
                <TableHead className="w-28">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.data?.items.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>
                    <SeverityBadge severity={a.severity} />
                  </TableCell>
                  <TableCell>{TYPE_KEYS[a.type] ? t(TYPE_KEYS[a.type]) : a.type}</TableCell>
                  <TableCell className="font-medium">
                    {a.tenant_name ?? t("ops.platformLabel")}
                  </TableCell>
                  <TableCell className="max-w-md">{a.message}</TableCell>
                  <TableCell className="tabular-nums">{a.count}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap text-muted">
                    {formatDateTime(a.updated_at)}
                    {a.status === "resolved" ? `（${t("ops.alertResolved")}）` : ""}
                  </TableCell>
                  <TableCell>
                    {a.status === "open" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setResolving(a);
                          setNote("");
                          setError(null);
                        }}
                      >
                        {t("ops.alertResolve")}
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={list.data?.total ?? 0}
            onPage={setPage}
          />
        </Card>
      )}

      <Dialog open={resolving !== null} onOpenChange={(o) => !o && setResolving(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t("ops.alertResolveTitle", {
                type: resolving
                  ? TYPE_KEYS[resolving.type]
                    ? t(TYPE_KEYS[resolving.type])
                    : resolving.type
                  : "",
              })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted">{resolving?.message}</p>
            <div className="space-y-1.5">
              <Label>{t("ops.alertResolveNote")}</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t("ops.alertResolveNotePlaceholder")}
              />
            </div>
            {error ? <p className="text-xs text-danger">{error}</p> : null}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResolving(null)}>
              {t("common.cancel")}
            </Button>
            <Button disabled={resolve.isPending || !resolving} onClick={() => resolve.mutate()}>
              {resolve.isPending ? t("common.processing") : t("common.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
